
/**
 * @fileoverview XML generation utility for Click&Pledge SOAP API requests.
 * Converts donation data into properly formatted XML for payment processing.
 */

import cookieStorage from './cookieStorage';

/**
 * Helper function to safely escape XML special characters.
 * Prevents XML injection and ensures proper formatting.
 *
 * @param {string|null|undefined} str - String to escape
 * @returns {string} Escaped string safe for XML
 */
const escapeXml = str => {
    if (str === null || str === undefined) {
        return '';
    }

    return String(str)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
};

const roundToHundredth = value => {
    const parsed = parseFloat(value);

    return isNaN(parsed) ? 0 : Math.round((parsed + Number.EPSILON) * 100);
};

/**
 * Generates XML data for Click&Pledge SOAP API payment processing.
 * Creates a complete SOAP envelope with all donation, payment, and user information.
 *
 * @param {Array} data - Form data array containing payment and donation information
 * @param {number} amount - Total donation amount
 * @param {string} recSelection - Recurring selection (e.g., "One Time Only", "Monthly")
 * @param {number} installments - Number of installments for recurring payments
 * @param {Object} userinfo - User information (name, email, etc.)
 * @param {Object} account - Account information (accountId, accountGuid, etc.)
 * @param {Object} matchingGifts - Matching gifts information
 * @param {Object} peerToPeer - Peer-to-peer fundraising information
 * @param {string} comments - Social comments from donor
 * @param {Object} conFeeOrder - Convenience fee order information
 * @param {string} recMethod - Recurring method type
 * @param {Object} campaignData - Campaign-specific data
 * @param {Object} singlePayItems - Single payment item details
 * @param {Array} MultiPayItems - Multiple payment items array
 * @param {number} multiPayTotalTaxDeduct - Total tax deductible for multi-pay
 * @param {number} defaultDonationAmount - Default donation amount
 * @param {boolean} isAnonymous - Whether donation is anonymous
 * @param {boolean} isInstallmentTouched - Whether installment was modified
 * @param {boolean} IsInDefiniteRecurring - Whether recurring is indefinite
 * @param {string} Tracker - Tracking information
 * @param {string} IPAddress - User's IP address
 * @param {string} Version - API version
 * @param {number} ReciptTaxdeductable - Tax deductible amount for receipt
 * @param {string} chargeDate - Date to charge the payment
 * @returns {string} Complete XML SOAP envelope for payment processing
 */
export const XMLData = ({
    data,
    amount,
    recSelection,
    installments,
    userinfo,
    account,
    peerToPeer,
    comments,
    conFeeOrder,
    recMethod,
    campaignData,
    singlePayItems,
    MultiPayItems,
    multiPayTotalTaxDeduct,
    defaultDonationAmount,
    isAnonymous,
    Tracker,
    IPAddress,
    Version,
    ReciptTaxdeductable,
    chargeDate
}) => {
    const mergedData = Array.isArray(data) ? Object.assign({}, ...data) : data || {};
    const newdata = mergedData;
    const paymentObj = newdata.payment || (Array.isArray(data) && data.find(d => d && d.payment)?.payment) || {};

    let originalAmount;

    if (parseFloat(newdata?.amountsection) === -1) {
        originalAmount = newdata?.other_amount && !isNaN(parseFloat(newdata.other_amount))
            ? parseFloat(newdata.other_amount).toFixed(2)
            : amount;
    } else if (newdata?.amountsection && !isNaN(parseFloat(newdata.amountsection))) {
        originalAmount = newdata.amountsection;
    } else if (defaultDonationAmount > 0) {
        originalAmount = recMethod === 'Subscription' ? defaultDonationAmount : amount;
    } else {
        originalAmount = amount;
    }

    // Check if we have a separate convenience fee
    const hasConFee = Object.keys(conFeeOrder || {}).length > 0 && conFeeOrder.postandcombained !== '1';
    const conFeeValue = hasConFee ? parseFloat(conFeeOrder.conFee) : 0;

    // Ensure OrderItem (donation) + OrderItem (fee) does not exceed Total amount
    if (hasConFee && parseFloat(amount) > conFeeValue) {
        if (parseFloat(originalAmount) >= parseFloat(amount) || isNaN(parseFloat(originalAmount))) {
            originalAmount = Math.max(0, parseFloat(amount) - conFeeValue).toFixed(2);
        }
    }

    const baseItemId = singlePayItems?.itemId || singlePayItems?.ItemID;
    let itemsID = baseItemId && !isNaN(parseInt(baseItemId, 10)) ? parseInt(baseItemId, 10) : 183000;
    let totalTax = 0;

    if (
        Object.keys(conFeeOrder).length
        && conFeeOrder.taxDeductible !== 100
        && Object.keys(singlePayItems).length
        && singlePayItems.taxDeductible !== 100
    ) {
        totalTax
            = parseFloat(singlePayItems.taxDeductible)
            + parseFloat(conFeeOrder.taxDeductible);
    }
    if (
        Object.keys(conFeeOrder).length
        && conFeeOrder.taxDeductible !== 100
        && MultiPayItems.length
        && multiPayTotalTaxDeduct !== 100
    ) {
        totalTax = multiPayTotalTaxDeduct + parseFloat(conFeeOrder.taxDeductible);
    }
    if (
        !Object.keys(conFeeOrder).length
        && Object.keys(singlePayItems).length
        && singlePayItems.taxDeductible !== 100
    ) {
        totalTax = parseFloat(singlePayItems.taxDeductible);
    }
    if (
        !Object.keys(conFeeOrder).length
        && MultiPayItems.length
        && multiPayTotalTaxDeduct !== 100
    ) {
        totalTax = multiPayTotalTaxDeduct;
    }

    const totalDeductible = parseFloat(totalTax);

    const MultiOrderItems = MultiPayItems.length
        ? MultiPayItems
            .filter(itm => itm.dItemPrice > 0)
            .map((itm, i) => `<OrderItem>
      <ItemID>${itemsID + i}</ItemID>
      <ItemName>${escapeXml(itm.dItemName)}</ItemName>
      <Quantity>1</Quantity>
      <UnitPrice>${roundToHundredth(itm.dItemPrice)}</UnitPrice>
      <UnitDeductible>${roundToHundredth(itm.taxDeductible)}</UnitDeductible>
      <SKU>${escapeXml(itm.dsku)}</SKU>
      <GAU>${escapeXml(itm.dgau)}</GAU>
      </OrderItem>`)
        : [];

    itemsID = MultiPayItems.length ? itemsID + 1 : itemsID;


    let xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:pay="https://ClickAndPledge.com/PaymentService/">
                <soapenv:Header/>
                    <soapenv:Body>
                      <pay:Operation>
                        <pay:instruction>
                        <![CDATA[<?xml version="1.0"?>
                        <CnPAPI xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="urn:APISchema.xsd">
                        <Version>${Version}</Version>
                        <Engine>
                        <Application>
                        <ID>MeetHour</ID>
                        <Name>MeetHourGivent</Name>
                        <Version>${Version}</Version>
                        </Application>
                        <Request>
                      <Operation>
                        <OperationType>Transaction</OperationType>
                        <IPAddress>${IPAddress}</IPAddress>
                        <DeclineFraud>false</DeclineFraud>
                        <UrlReferrer />
                        ${cookieStorage.getItem(`CnP_IPQS_${account.accountId}`) ? `<TrackingId>${cookieStorage.getItem(`CnP_IPQS_${account.accountId}`)}</TrackingId>` : ''}
                    </Operation>
                <Authentication>
                    <AccountGuid>${account.accountGuid}</AccountGuid>
                    <AccountID>${account.accountId}</AccountID>
                </Authentication>

                    <Order>
                    <OrderMode>Production</OrderMode>
                    ${Tracker ? `<Tracker>${escapeXml(Tracker)}</Tracker>` : ''}
                <CardHolder>`;

    userinfo
        && (xml += `<BillingInformation>
            <BillingFirstName>${escapeXml(userinfo.first_name || userinfo.display_name)}</BillingFirstName>
            <BillingMI>${userinfo.txtmi ? userinfo.txtmi : ''}</BillingMI>
            <BillingLastName>${escapeXml(userinfo.last_name)}</BillingLastName>
            <BillingEmail>${userinfo.email ? userinfo.email : ''}</BillingEmail>
            </BillingInformation>
            `);
    paymentObj
        && paymentObj.zipcode
        && (xml += `<BillingAddress>
                <BillingPostalCode>${escapeXml(paymentObj.zipcode)}</BillingPostalCode>
            </BillingAddress>`);

    isAnonymous
        && (xml += `<CustomFieldList>
                     <CustomField>
                       <FieldName>Anonymous Donation</FieldName>
                       <FieldValue>Yes</FieldValue>
                       </CustomField>
                       </CustomFieldList>
                        
                    `);

    paymentObj
        && (paymentObj.ccn || paymentObj.noc)
        && (xml += `<PaymentMethod>
            <PaymentType>CreditCard</PaymentType>
            <CreditCard>
                <NameOnCard>${escapeXml(paymentObj.noc || '')}</NameOnCard>
                <CardNumber>${escapeXml(String(paymentObj.ccn || '').replace(/\D/g, ''))}</CardNumber>
                <Cvv2>${escapeXml(paymentObj.cvv || '')}</Cvv2>
                <ExpirationDate>${paymentObj.expiryMonth && paymentObj.expiryYear
                ? `${paymentObj.expiryMonth}/${paymentObj.expiryYear}`
                : ''}</ExpirationDate>
                </CreditCard>
            </PaymentMethod>`);

    xml += `<CustomParameters>
             
              <Parameter>
              <Field>SocialComment</Field>
              <Value>${escapeXml(comments)}</Value>
              </Parameter>

              <Parameter>
              <Field>SocialCommentStatus</Field>
              <Value>${campaignData?.socialCommentsStatus || ''}</Value>
              </Parameter>
              </CustomParameters>`;
    xml += `</CardHolder>
            
                    <OrderItemList>
                    ${MultiPayItems.length
        ? MultiOrderItems.join('')
        : `<OrderItem>
                        <ItemID>${itemsID}</ItemID>
                        <ItemName>${singlePayItems?.dItemName ? escapeXml(singlePayItems.dItemName) : 'General Donation'}</ItemName>
                        <Quantity>1</Quantity>
                        <UnitPrice>${roundToHundredth(conFeeOrder?.postandcombained === '1' ? amount : originalAmount)}</UnitPrice>
                        <UnitDeductible>${roundToHundredth(conFeeOrder?.postandcombained === '1' ? totalDeductible : singlePayItems?.taxDeductible || 0)}</UnitDeductible>
                        <SKU>${(() => {
        if (Object.keys(conFeeOrder || {}).length && conFeeOrder?.postandcombained === '1') {
            return escapeXml((singlePayItems?.dsku || '') + (conFeeOrder?.conSKU || ''));
        }

        return escapeXml(singlePayItems?.dsku);
    })()}</SKU>
                        <GAU>${(() => {
        if (Object.keys(conFeeOrder || {}).length && conFeeOrder?.postandcombained === '1') {
            return escapeXml((singlePayItems?.dgau || '') + (conFeeOrder?.conGAU || '')).substring(0, 80);
        }

        return escapeXml(singlePayItems?.dgau).substring(0, 80);
    })()}</GAU>
                        </OrderItem>`
}
                    `;
    Object.keys(conFeeOrder || {}).length
        && conFeeOrder.postandcombained !== '1'
        && (xml += ` <OrderItem>
              <ItemID>${conFeeOrder.itemId || (itemsID + 1)}</ItemID>
              <ItemName>${escapeXml(conFeeOrder.conFeeItemName)}</ItemName>
              <Quantity>1</Quantity>
                            <UnitPrice>${roundToHundredth(conFeeOrder.conFee)}</UnitPrice>
                            <UnitDeductible>${roundToHundredth(conFeeOrder.taxDeductible)}</UnitDeductible>
              ${conFeeOrder.conSKU
                ? `<SKU>${escapeXml(conFeeOrder.conSKU)}</SKU>`
                : '<SKU />'
            }
               ${conFeeOrder.conGAU
                ? `<GAU>${escapeXml(conFeeOrder.conGAU).substring(0, 80)}</GAU>`
                : '<GAU />'
            }
            </OrderItem>`);

    xml += '</OrderItemList>';

    xml += `<Receipt>
                        <SendReceipt>true</SendReceipt>
                        ${isAnonymous ? '<Anonymous>true</Anonymous>' : ''}
                        <Deductible>${ReciptTaxdeductable}</Deductible>
                    </Receipt>`;

    xml += `<Transaction>
                        <TransactionType>Payment</TransactionType>`;

    recSelection
        && recSelection !== 'One Time Only'
        && (xml += `<Recurring>
                            ${installments
            && `<Installment>${installments}</Installment>`
            }
                            <Periodicity>${recSelection}</Periodicity>
                            <RecurringMethod>${recMethod}</RecurringMethod>
                            </Recurring>`);

    xml += `<CurrentTotals>
                            <TotalDeductible>${roundToHundredth(totalDeductible)}</TotalDeductible>
                            <Total>${roundToHundredth(amount)}</Total>
                        </CurrentTotals>`;

    chargeDate && (xml += `<ChargeDate>${chargeDate}</ChargeDate>`);

    xml += `<CustomParameters>
               ${account
        && account.GiventID
        && `<Parameter>
                  <Field>GiventID</Field>
                  <Value>${account.GiventID}</Value>
                  </Parameter>
                  `
}
                ${campaignData
        && campaignData.campaignID
        && `<Parameter>
                  <Field>ConnectCampaignId</Field>
                  <Value>${campaignData.campaignID}</Value>
                  </Parameter>
                  `
}
    
                ${campaignData
        && campaignData.payWidgetID
        && `<Parameter>
                    <Field>PaymentWidgetID</Field>
                    <Value>${campaignData.payWidgetID}</Value>
                  </Parameter>
                  `
}
                
                           
              </CustomParameters>`;

    peerToPeer
        && peerToPeer.fundraiserEmail
        && (xml += `<FundraiserEmail>${peerToPeer.fundraiserEmail}</FundraiserEmail>
                    `);
    peerToPeer
        && peerToPeer.teamURL
        && (xml += `<TeamURLAlias>${peerToPeer.teamURL}</TeamURLAlias> 
                          `);


    xml += ` </Transaction>
  </Order>
</Request>
</Engine>
</CnPAPI>]]>
</pay:instruction>
</pay:Operation>
</soapenv:Body>
</soapenv:Envelope>
`;

    return xml;
};
